import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import theme from "../../../../Themes/defaultTheme";
import { handleOptionSetValue } from "../../../../../Utils/Helpers/optionSet.helper";
import {
  customDateFormat,
  FromEpochFormat,
} from "../../../../../Utils/Helpers/dateUtils";
import A_FormHelper from "../../DataDisplay/Tooltips/A_FormHelper";
import { InputAdornment } from "@material-ui/core";

const useStyles = makeStyles(() => ({
  formControl: {
    width: "222px",
    marginTop: 5,
    "& .MuiInputLabel-root": {
      color: "var(--color-text-secondary)",
      "&.Mui-focused": {
        color: "var(--color-primary-text)",
      },
    },
    "& .MuiInputBase-root": {
      color: "var(--color-text-primary) !important",
      "&::before": {
        borderBottomColor: "var(--total_row_boldbackground)",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "var(--color-primary-main)",
      },
      "&::after": {
        borderBottomColor: "var(--color-primary-main)",
      },
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "var(--color-background-paper)",
      borderRadius: "calc(10px * var(--app-radius-scale, 1))",
      "& fieldset": {
        borderColor: "var(--total_row_boldbackground)",
      },
      "&:hover fieldset": {
        borderColor: "var(--color-primary-main)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "var(--color-primary-main)",
      },
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "var(--total_row_boldbackground)",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: "var(--color-primary-main)",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "var(--color-primary-main)",
    },
    "& .MuiFormHelperText-root": {
      color: "var(--color-text-secondary)",
    },
  },
  selectLabel: {
    marginTop: "-1.8px",
  },
  fullWidth: {
    width: "100%",
  },
  select: {
    fontSize: "0.9375rem",
    height: "30px",
    "& .MuiSelect-icon": {
      color: "var(--color-text-secondary)",
      'html[data-mode="dark"] &': {
        color: "var(--color-text-primary) !important",
      },
    },
    "& .MuiSelect-select": {
      color: "var(--color-text-primary)",
    },
  },
  minWidth: {
    width: "180px",
  },
  helperIcon: {
    "& svg": {
      marginRight: "20px",
    },
    "& .helperIcon": {
      marginRight: "0px",
    },
  },
  menuPaper: {
    backgroundColor: "var(--color-background-paper) !important",
    color: "var(--color-text-primary) !important",
    borderRadius: "calc(12px * var(--app-radius-scale, 1)) !important",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4) !important",
    "& .MuiMenuItem-root": {
      fontSize: "0.875rem",
      padding: "10px 16px",
      color: "var(--color-text-primary) !important",
      transition: "background-color 0.2s ease, color 0.2s ease",
      "&:hover": {
        backgroundColor: "var(--total_row_background) !important",
        color: "var(--color-primary-text) !important",
      },
      "&.Mui-selected": {
        backgroundColor: "var(--total_row_boldbackground) !important",
        color: "var(--color-primary-text) !important",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "var(--total_row_boldbackground) !important",
          opacity: 0.9,
        },
      },
    },
    "& .MuiListSubheader-root": {
      color: "var(--color-text-secondary) !important",
      backgroundColor: "var(--color-background-paper) !important",
      lineHeight: "36px",
      fontWeight: 700,
      fontSize: "0.6875rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  },
}));

const A_Select = (props) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(
    handleOptionSetValue(props.defaultValue, false, true)
  );
  const handleChange = (event) => {
    if (event.target.value !== "") {
      let selectedOption = props.options.filter(function (option) {
        return handleOptionSetValue(option.value, false) == event.target.value;
      })[0];
      setValue(selectedOption.value);
      props.onChange(selectedOption, props.id, false);
    } else {
      setValue(event.target.value);
      props.onChange({ value: event.target.value }, props.id);
    }
  };

  let options = props.options.map((option) => (
    <MenuItem
      key={handleOptionSetValue(option.value, false)}
      value={handleOptionSetValue(option.value, false)}
    >
      {handleOptionSetValue(option.label)}
    </MenuItem>
  ));

  if (
    props.label === "Date" &&
    typeof props.defaultValue == "number" &&
    `${props.defaultValue}`.length >= 10 &&
    options.every((option) => {
      return !customDateFormat(
        FromEpochFormat(props.defaultValue),
        "MMMM YYYY"
      ).includes(option.props.children);
    })
  ) {
    let dateOption = customDateFormat(
      FromEpochFormat(props.defaultValue),
      "MMMM YYYY"
    );
    dateOption &&
      options.push(
        <MenuItem
          key={handleOptionSetValue(dateOption, false, true)}
          value={handleOptionSetValue(dateOption, false, true)}
        >
          {handleOptionSetValue(dateOption, false, true)}
        </MenuItem>
      );
  }
  
  let showGroupedOptions = () => {
    let optionsList = [];
    props.groupedOptions.forEach((groupedOption) => {
      optionsList.push(
        <ListSubheader key={groupedOption.title} disableSticky>
          {groupedOption.title}
        </ListSubheader>
      );
      if (groupedOption.options) {
        groupedOption.options.forEach((option) => {
          optionsList.push(
            <MenuItem
              key={handleOptionSetValue(option.value, false)}
              value={handleOptionSetValue(option.value, false)}
            >
              {handleOptionSetValue(option.label)}
            </MenuItem>
          );
        });
      }
    });
    return optionsList;
  };
  return (
    <ThemeProvider theme={theme}>
      <FormControl
        error={props.error}
        key={"formcontrol" + props.id}
        id={"formcontrol" + props.id}
        variant={props.variant}
        className={`${
          props.className ? props.className : classes.formControl
        } ${props.fullWidth ? classes.fullWidth : ""} ${
          props.minWidth ? classes.minWidth : ""
        }`}
        required={props.required}
        style={props.style}
      >
        <InputLabel
          className={classes.selectLabel}
          key={props.id + "_label"}
          id={props.id + "_label"}
          {...(props.InputLabelProps || {})}
        >
          {props.label}
        </InputLabel>
        <Select
          key={props.id}
          id={props.id}
          label={props.variant === "outlined" ? props.label : undefined}
          labelId={props.id + "_label"}
          value={
            props.isCascading
              ? handleOptionSetValue(props.defaultValue, false, true)
              : value && handleOptionSetValue(value)
          }
          native={false}
          style={props.style}
          //variant={props.variant}
          endAdornment={
            <InputAdornment position="end">
              <A_FormHelper fieldHelperText={props.fieldHelperText} />
            </InputAdornment>
          }
          disabled={props.disabled}
          required={props.required}
          autoWidth={props.autoWidth}
          displayEmpty={props.displayEmpty}
          inputProps={{
            readOnly: props.readOnly,
            style:
              props.inputProps && props.inputProps != ""
                ? props.inputProps
                : {},
          }}
          color={props.color}
          className={`${props.className} ${classes.select} ${
            props.fieldHelperText ? classes.helperIcon : ""
          }`}
          onChange={handleChange}
          onKeyDown={(e) => e.stopPropagation()}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            classes: {
              paper: classes.menuPaper,
            },
          }}
        >
          {props.groupedOptions ? showGroupedOptions() : options}
        </Select>
        <FormHelperText key={"helptext" + props.id} id={"helptext" + props.id}>
          {props.helperText}
        </FormHelperText>
      </FormControl>
    </ThemeProvider>
  );
};
A_Select.defaultProps = {
  id: "xxxx",
  //label: "Select",
  value: "",
  variant: "outlined",
  color: "primary",
  inputProps: "",
  disabled: false,
  autoWidth: false,
  displayEmpty: true,
  error: false,
  required: false,
  options: [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ],
  native: false,
  style: {},
  fieldHelperText: "",
  isCascading: false,
};
A_Select.propTypes = {
  variant: PropTypes.oneOf(["filled", "outlined", "standard"]),
  color: PropTypes.oneOf(["default", "inherit", "primary", "secondary"]),
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  autoWidth: PropTypes.bool,
  displayEmpty: PropTypes.bool,
  onClick: PropTypes.func,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  helperText: PropTypes.string,
  className: PropTypes.any,
  id: PropTypes.any,
  label: PropTypes.string,
  options: PropTypes.array,
  native: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.any,
  groupedOptions: PropTypes.any,
  error: PropTypes.any,
  style: PropTypes.any,
  fullWidth: PropTypes.any,
  minWidth: PropTypes.any,
  optionSet: PropTypes.any,
  inputProps: PropTypes.any,
  fieldHelperText: PropTypes.string,
  isCascading: PropTypes.bool,
  InputLabelProps: PropTypes.object,
};
export default A_Select;
